import {
  FinanceRate,
  Job,
  Lead,
  Payment,
} from '@kanda-libs/ks-frontend-services';

import { checkHasSecondLine } from 'pages/Quote/helpers';
import { FormattedJob } from 'types';

import type { CheckoutFinanceRate, FormattedRates } from './constants';

export const getMinDeposit = (rate: CheckoutFinanceRate) => rate.minDeposit;

const rateIsEnabled = (rate: FinanceRate, job: Job, deposit: number) => {
  const jobTotal = job.total?.amount_vat_inclusive?.amount || 0;
  const loan = jobTotal - deposit;

  const {
    credit_line: creditLine,
    min_deposit_pct: minDepPct,
    max_deposit_pct: maxDepPct,
    min_job_value: minLoan,
    max_job_value: maxLoan,
    max_deposit_value: maxDepVal = 0,
    min_deposit_value: minDepVal = 0,
  } = rate;

  const minDep = Math.max(
    ((minDepPct || 0) * jobTotal) / 10000,
    minDepVal || 0,
  );
  const maxDep = Math.min(
    ((maxDepPct || 0) * jobTotal) / 10000,
    maxDepVal > 0 ? (maxDepVal as number) : jobTotal,
  );

  const validDeposit = deposit >= minDep && deposit <= maxDep;
  const validLoan = loan >= (minLoan || 0) && loan <= (maxLoan || jobTotal);
  const validCreditLine = creditLine ? loan <= creditLine : true;

  return validDeposit && validLoan && validCreditLine;
};

const convertRate = (rate: FinanceRate): CheckoutFinanceRate => ({
  interest: String(rate?.apr || 0),
  duration: String(rate?.duration || 0),
  minDeposit: rate?.min_deposit_pct || 0,
  maxDeposit: rate?.max_deposit_pct || 50,
  minLoan: rate?.min_job_value || 0,
  maxLoan: rate?.max_job_value || 1500000,
  provider: rate.provider,
  name: rate.name,
  type: rate?.apr_type || 'INTEREST_FREE',
  workTypes: rate?.work_types || [],
  deferredDuration: rate?.deferred_duration,
  creditLine: rate?.credit_line,
  enabled: rate.enabled,
});

export const formatRates = (
  job?: FormattedJob,
  lead?: Lead,
  loanAmount?: string,
  depositAmount?: string,
  bnpl = false,
): FormattedRates | [] => {
  if (!job) return [];
  if (!loanAmount || !depositAmount) return [];
  const secondLine =
    job?.finance_status === 'finance_not_approved' && checkHasSecondLine(job);
  const deposit = parseInt(depositAmount, 10);

  const rates = secondLine
    ? job.finance_options?.filter((rate: FinanceRate) => rate.enabled)
    : lead?.finance_options || [];
  if (!rates) return [];
  const aprs: number[] = rates
    ?.filter((rate: FinanceRate) =>
      bnpl
        ? rate.apr_type === 'BUYNOW_PAYLATER'
        : rate.apr_type !== 'BUYNOW_PAYLATER',
    )
    ?.map((rate: FinanceRate) => rate.apr || 0)
    .filter(
      (apr: number, index: number, arr: number[]) => arr.indexOf(apr) === index,
    );
  return aprs.reduce((final: FormattedRates, apr: number) => {
    const aprRates: CheckoutFinanceRate[] = rates
      .filter((rate: FinanceRate) =>
        bnpl
          ? rate.apr_type === 'BUYNOW_PAYLATER'
          : rate.apr_type !== 'BUYNOW_PAYLATER',
      )
      .filter((rate: FinanceRate) => rate.apr === apr)
      .map((rate: FinanceRate) => ({
        ...convertRate(rate),
        disabled: !rateIsEnabled(rate, job, deposit),
      }))
      .sort(
        (rate1: CheckoutFinanceRate, rate2: CheckoutFinanceRate) =>
          parseInt(rate1?.duration, 10) - parseInt(rate2?.duration, 10),
      );
    return {
      ...final,
      [String(apr === 0 ? 0 : apr / 100)]: aprRates,
    };
  }, {});
};

export const getDepositPayments = (
  job: FormattedJob | Job,
): Payment[] | undefined =>
  job?.payments
    ?.filter(
      (payment: Payment) => payment.payment_option.payment_method !== 'loan',
    )
    ?.sort(
      (p1: Payment, p2: Payment) =>
        new Date(p2.metadata?.created_at || 0).getTime() -
        new Date(p1.metadata?.created_at || 0).getTime(),
    );

export const getLoanPayments = (
  job: FormattedJob | Job,
): Payment[] | undefined =>
  job?.payments
    ?.filter(
      (payment: Payment) => payment.payment_option.payment_method === 'loan',
    )
    ?.sort(
      (p1: Payment, p2: Payment) =>
        new Date(p2.metadata?.created_at || 0).getTime() -
        new Date(p1.metadata?.created_at || 0).getTime(),
    );

export const checkHasCredit = (
  job?: FormattedJob | Job,
  lead?: Lead,
): boolean => {
  if (!job || !lead) return false;
  const loanPayments = getLoanPayments(job);
  return Boolean(loanPayments?.[0]);
};
